/* eslint-disable react-hooks/exhaustive-deps */
import * as yup from "yup";
import { Button, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import AutoComplete from "../../../../components/hookForms/autoComplete";
import TextInput from "../../../../components/hookForms/textField";
import messages from "../../../../utils/messages";
import serviceClienteInstalacao from "../../../../services/serviceClienteInstalacao";
import InputNumber from "@/components/hookForms/inputNumber";

export interface IDialogProps {
    open: boolean;
    onSuccess: () => void;
    data: {
        open: boolean;
        id: string;
        nomeCliente: string;
        numeroInstalacao: string;
        regraSaldoReenergisa: string;
        percentualDescontoContratado: string;
        percentualDescontoFaturado: string;
        flagHabilitado: string;
        regraDataVencimento?: string;
        dataFixa?: number | null;
    };
    onClose?: any;
}

export default function DialogEdit({ open, data, onClose, onSuccess }: IDialogProps) {
    const [isDisable, setDisable] = useState(false);
    const dayRangeMessage = "O dia deve estar entre 1 e 31";

    const isValidDay = (value?: string) => {
        if (!value) {
            return false;
        }

        const numericValue = Number(value.toString().replaceAll(".", "").replaceAll(",", "."));
        return Number.isInteger(numericValue) && numericValue >= 1 && numericValue <= 31;
    };

    const validationSchema = yup.object().shape({
        regraSaldoReenergisa: yup.string().required(messages.requiredField),
        flagHabilitado: yup.string().required(messages.requiredField),
        dataFixa: yup
            .string()
            .when("regraDataVencimento", {
                is: (value: string) => value === "Data fixa",
                then: yup
                    .string()
                    .required(messages.requiredField)
                    .test("valid-day", dayRangeMessage, (value) => isValidDay(value)),
                otherwise: yup.string().nullable(),
            }),
    });

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
        // eslint-disable-next-line react-hooks/rules-of-hooks
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            regraSaldoReenergisa: "",
            percentualDescontoFaturado: "",
            flagHabilitado: "",
            regraDataVencimento: "",
            dataFixa: "",
        },
    });

    const regraDataVencimentoValue = watch("regraDataVencimento");
    const isDataFixa = regraDataVencimentoValue === "Data fixa";

    useEffect(() => {
        if (data) {
            reset({
                ...data,
                regraDataVencimento: data.regraDataVencimento || "",
                dataFixa: data.dataFixa ? data.dataFixa.toString() : "",
            });
        }
    }, [data, reset]);

    const onSubmit = (values: any) => {
        setDisable(true);

        const parsedDataFixa = values.dataFixa
            ? Number(values.dataFixa.toString().replaceAll(".", "").replaceAll(",", "."))
            : null;
        const dataFixaValue = values.regraDataVencimento === "Data fixa" && parsedDataFixa ? parsedDataFixa : null;

        const updateValues = {
            ...values,
            clienteInstalacaoID: data.id,
            percentualDescontoFaturado: values.percentualDescontoFaturado
                ? Number(values.percentualDescontoFaturado.replaceAll(".", "").replaceAll(",", "."))
                : null,
            dataFixa: dataFixaValue,
        };

        serviceClienteInstalacao
            .update(updateValues)
            .then((result) => {
                if (result) {
                    toast.success(messages.updateSucess);
                    onClose(true);
                    onSuccess();
                }
            })
            .catch((err) => {
                toast.error(messages.updateError, err);
            })
            .finally(() => {
                setDisable(false);
            });
    };

    const regraReenergisaOptions = [
        { label: "Sempre", value: "Sempre" },
        { label: "Somente Primeira Injeção", value: "Somente Primeira Injeção" },
        { label: "Nunca", value: "Nunca" },
    ];

    const habilitadoOptions = [
        { label: "Sim", value: "Sim" },
        { label: "Não", value: "Não" },
    ];

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            onClose={(_, reason) => {
                if (reason !== "backdropClick") {
                    onClose();
                }
            }}
            open={open}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>
                    Editar
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            reset();
                            onClose(true);
                        }}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={12}>
                            <TextInput name="id" label="ID da instalação" control={control} disabled={true} />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextInput
                                id="numeroInstalacao"
                                name="numeroInstalacao"
                                label="Instalação"
                                control={control}
                                disabled={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextInput
                                id="cliente"
                                name="nomeCliente"
                                label="Cliente"
                                control={control}
                                disabled={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <AutoComplete
                                id="regraSaldoReenergisa"
                                name="regraSaldoReenergisa"
                                label="Regra Saldo Não Pago"
                                options={regraReenergisaOptions}
                                control={control}
                                error={errors}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextInput
                                id="descontoContratado"
                                name="percentualDescontoContratado"
                                label="Desconto %"
                                control={control}
                                disabled={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputNumber
                                id="descontoFaturado"
                                name="percentualDescontoFaturado"
                                label="Desconto Faturamento %"
                                control={control}
                                error={errors}
                                decimalScale={2}
                                maxLength={9}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <AutoComplete
                                id="habilitado"
                                name="flagHabilitado"
                                label="Habilitado"
                                options={habilitadoOptions}
                                control={control}
                                error={errors}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextInput
                                id="regraDataVencimento"
                                name="regraDataVencimento"
                                label="Regra Data de Vencimento"
                                control={control}
                                disabled
                            />
                        </Grid>
                        {isDataFixa && (
                            <Grid item xs={12} md={12}>
                                <InputNumber
                                    id="dataFixa"
                                    name="dataFixa"
                                    label="Dia Fixo (1 a 31)"
                                    control={control}
                                    error={errors}
                                    decimalScale={0}
                                    maxLength={2}
                                    allowNegative={false}
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions style={{ display: "flex", justifyContent: "center", margin: "24px" }}>
                    <Button
                        onClick={onClose}
                        color="warning"
                        variant="contained"
                        style={{ width: "159px", height: "44px" }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={isDisable}
                        style={{ width: "159px", height: "44px" }}
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
